import type { FormField } from "@tellescope/types-models";
import { collectFieldPath, type FieldPathWalkContext } from "./walk-field-path";
import { occupiesOwnPage } from "./question-group";
import type { FormFieldNode } from "./types";

/**
 * How many pages the form has along the path the current answers select.
 *
 * Counts only fields that get their own page, per {@link occupiesOwnPage}.
 * Conditional branches are resolved rather than enumerated, so this changes as
 * answers change. Never returns less than 1, so a form of only invisible fields
 * still reads as a single page.
 *
 * @param subFieldIds ids belonging to a Question Group
 * @param fieldsMap all fields by id, used to resolve a group's sub-field types
 */
export const countFormPages = (
  fieldTree: FormFieldNode,
  subFieldIds: ReadonlySet<string>,
  walkContext: FieldPathWalkContext,
  fieldsMap: Record<string, FormField | undefined>
): number => {
  const path = collectFieldPath(fieldTree, walkContext);
  const count = path.filter((node) => occupiesOwnPage(node.value, subFieldIds, fieldsMap)).length;
  return Math.max(count, 1);
};

/**
 * Longest number of pages still ahead of `field`, following the raw field graph.
 *
 * Unlike {@link countFormPages} this ignores conditional logic and takes the
 * deepest branch, giving an upper bound for progress display when the actual route
 * isn't yet known. A run of `Hidden Value` children adds no page but is traversed
 * through to whatever follows it.
 *
 * @param allFields every field on the form, used to find children via `previousFields`
 * @param subFieldIds ids belonging to a Question Group
 */
export const countRemainingFormPages = (
  field: FormField,
  allFields: FormField[],
  subFieldIds: ReadonlySet<string>
): number => {
  const countRemaining = (current: FormField, explored: string[]): number => {
    if (explored.includes(current.id)) return 0;
    explored.push(current.id);

    const children = allFields.filter(
      (f) => f.previousFields.find((p) => p.type !== "root" && p.info.fieldId === current.id) && !subFieldIds.has(f.id)
    );
    if (children.length === 0) return 0;

    const hiddenChildren = children.filter((c) => c.type === "Hidden Value");
    const visibleChildren = children.filter((c) => c.type !== "Hidden Value");

    if (visibleChildren.length === 0) {
      return Math.max(...hiddenChildren.map((c) => countRemaining(c, explored)));
    }

    return 1 + Math.max(...children.map((c) => countRemaining(c, explored)));
  };

  return countRemaining(field, []);
};
